import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Copy, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const EXCLUDE = ["/admin", "/pay", "/payment", "/login", "/register", "/dashboard"];
const ARM_MS = 8000; // only arm after the visitor has actually browsed a bit
const CODE = "STAY10";

export default function ExitIntent() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(10);

  useEffect(() => {
    try { if (sessionStorage.getItem("inj_exit_seen")) return undefined; } catch { return undefined; }
    if (EXCLUDE.some((p) => loc.pathname.startsWith(p))) return undefined;
    let armed = false;
    const arm = setTimeout(() => { armed = true; }, ARM_MS);
    const onOut = (e) => {
      if (!armed || e.relatedTarget || e.clientY > 8) return;
      document.removeEventListener("mouseout", onOut);
      try { sessionStorage.setItem("inj_exit_seen", "1"); } catch { /* ignore */ }
      api.get("/codes/validate", { params: { code: CODE } })
        .then((r) => { setPct(r.data.discount_pct); setOpen(true); })
        .catch(() => {});
    };
    document.addEventListener("mouseout", onOut);
    return () => { clearTimeout(arm); document.removeEventListener("mouseout", onOut); };
  }, [loc.pathname]);

  const copy = () => {
    navigator.clipboard.writeText(CODE);
    toast.success(`${CODE} copied — apply it at checkout`);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-sm flex items-center justify-center px-5" data-testid="exit-intent-modal">
      <div className="relative w-full max-w-lg rounded-2xl border border-white/20 bg-black p-10 sm:p-12">
        <button
          onClick={() => setOpen(false)}
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <div className="font-mono text-[10px] tracking-[0.3em] text-white/40">{"// BEFORE YOU GO"}</div>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl uppercase leading-none">Hold on, operative.</h2>
        <p className="mt-4 text-sm text-white/55 leading-relaxed">
          Take {pct}% off your first order — one-time code, auto-applies at checkout.
        </p>
        <div className="mt-8 flex items-center justify-between rounded-xl border border-white/30 bg-white/[0.04] px-5 py-4">
          <span className="font-mono text-xl tracking-[0.3em]" data-testid="exit-intent-code">{CODE}</span>
          <button onClick={copy} data-testid="exit-intent-copy-btn" className="text-white/50 hover:text-white transition-colors" title="Copy code">
            <Copy size={18} />
          </button>
        </div>
        <Link
          to="/store"
          onClick={() => setOpen(false)}
          data-testid="exit-intent-cta"
          className="btn-inj mt-6 flex items-center justify-center px-8 py-4 text-sm"
        >
          CLAIM {pct}% OFF — BROWSE STORE
        </Link>
        <div className="mt-4 text-center font-mono text-[9px] tracking-[0.25em] text-white/30">ONE USE PER BUYER · INSTANT KEY DELIVERY</div>
      </div>
    </div>
  );
}
